// ProtectedRoute — gates an authenticated subtree.
//
// Behaviour:
//   - status === 'loading'        → render a neutral loading view
//   - status === 'anonymous'      → redirect to /login?redirect=<original>
//   - status === 'authenticated'  → render children (or <Outlet /> when used
//     as a layout route)
//
// Token expiry is handled by the axios interceptor (silent refresh). If
// refresh fails the interceptor dispatches the logout event and the store
// flips to 'anonymous' — which lands the user here on the next render.

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuthStore } from '../store/authStore';

interface ProtectedRouteProps {
  children?: ReactNode;
}

// Weighbridge Kiosk Mode — operators only ever use the kiosk. The kiosk
// route (/weighbridge/kiosk) lives OUTSIDE this guard, so redirecting an
// authenticated operator there confines them to the kiosk and never loops.
const KIOSK_PATH = '/weighbridge/kiosk';

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const status = useAuthStore((s) => s.status);
  const role = useAuthStore((s) => s.user?.role);
  const location = useLocation();

  if (status === 'loading') {
    return <ProtectedLoading />;
  }

  if (status === 'anonymous') {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  // A weighbridge_operator who lands on any main-module route is bounced to
  // the kiosk — they have no access to the office surface.
  if (role === 'weighbridge_operator' && location.pathname !== KIOSK_PATH) {
    return <Navigate to={KIOSK_PATH} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}

function ProtectedLoading() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-page-bg, #f8fafc)',
        color: 'var(--color-text-secondary, #475569)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      Loading…
    </div>
  );
}
